import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  Avatar,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Chat,
  Close,
  Send,
  EventAvailable,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { getLanguage } from '@/utils/i18n';
import EmbedAPIClient from '@embedapi/core';
import { Property, NotificationType, NotificationPriority } from '@/types';
import { NotificationService } from '@/services/notificationService';

const embedClient = new EmbedAPIClient('01xJgJ_bGEWTQjG6O2xsEuN6TqGk9LN0');

interface Message {
  id: string;
  text: string;
  sender: 'ai' | 'user';
  timestamp: Date;
}

interface Props {
  property: Property;
}

const PropertyAISalesmanWidget: React.FC<Props> = ({ property }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const lang = getLanguage();
  
  const getInitialMessage = () => {
    if (lang === 'fr') return `Bonjour! Je suis l'assistant dédié pour "${property.metadata.title}". Avez-vous des questions spécifiques ou souhaitez-vous réserver une visite ?`;
    if (lang === 'ar') return `سلام! أنا المساعد الخاص بـ "${property.metadata.title}". واش عندك شي أسئلة ولا بغيتي تحجز زيارة؟`;
    return `Hello! I'm the dedicated advisor for "${property.metadata.title}". Do you have any specific questions or would you like to book a visit?`;
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: getInitialMessage(),
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInputValue('');
    setIsTyping(true);

    try {
      const apiMessages = updatedMessages.map(msg => ({
        role: msg.sender === 'ai' ? 'assistant' : 'user',
        content: msg.text
      }));

      const systemPrompt = `You are a dedicated real estate sales assistant on souk.immo for "${property.metadata.title}" priced at ${property.metadata.price} ${property.metadata.currency}. Type: ${property.metadata.propertyType}. 
      Your goal: Answer questions about the property AND collect visitor name, phone, and email to book a visit.
      IMPORTANT: If the user provides their name, phone, and email AND wants to book a visit, you MUST end your response with exactly this tag: [BOOK_VISIT: {name}, {phone}, {email}].
      Respond in language code: ${lang}.`;

      const result = await embedClient.generate({
        service: 'openai',
        model: 'gpt-4o-mini',
        messages: [{ role: 'system', content: systemPrompt }, ...apiMessages],
      });
      
      let aiResponseText = result?.choices?.[0]?.message?.content || result?.answer || "I apologize, but I am unable to respond at this moment.";

      // Handle Booking Intent
      if (aiResponseText.includes('[BOOK_VISIT:')) {
        const match = aiResponseText.match(/\[BOOK_VISIT: (.*?)\]/);
        if (match) {
          const bookingInfo = match[1];
          // Create notification for the agent
          await NotificationService.createNotification(
            property.agentId,
            NotificationType.APPOINTMENT,
            "New Visit Request",
            `A user wants to book a visit for ${property.metadata.title}. Contact: ${bookingInfo}`,
            NotificationPriority.HIGH,
            { propertyId: property.id, bookingInfo }
          );
          // Clean up the response text for the user
          aiResponseText = aiResponseText.replace(/\[BOOK_VISIT: .*?\]/, "").trim();
          aiResponseText += lang === 'fr' 
            ? "\n\n✅ Votre demande de visite a été transmise ! Un agent vous contactera bientôt." 
            : lang === 'ar'
            ? "\n\n✅ تم إرسال طلب الزيارة ديالك! غادي يتصل بك الوكيل قريبا."
            : "\n\n✅ Your visit request has been sent! An agent will contact you shortly.";
        }
      }

      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponseText,
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMsg]);
      
      // Artificial "Booking logic hook". If the AI detects intent and returns a specific flag, 
      // we could pop open a calendar or trigger an email. For now, the prompt implies "take user info and recall".
    } catch (error) {
      console.error('Error calling AI Property function:', error);
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I am currently experiencing technical difficulties. Please try again later.",
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 2000 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
          >
            <Paper
              elevation={6}
              sx={{
                width: { xs: 'calc(100vw - 64px)', sm: 380 },
                height: 550,
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 4,
                border: '1px solid rgba(197, 160, 89, 0.4)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              }}
            >
              {/* Header */}
              <Box sx={{ p: 2, bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'white' }}>
                    <EventAvailable sx={{ color: 'secondary.main' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Sales Advisor</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Booking & Info</Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Context bar */}
              <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Subject: {property.metadata.title} ({property.metadata.price} {property.metadata.currency})
                </Typography>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#F0EDE9' }}>
                <List disablePadding>
                  {messages.map((msg) => (
                    <ListItem
                      key={msg.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: msg.sender === 'ai' ? 'flex-start' : 'flex-end',
                        mb: 2,
                        px: 0,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '85%',
                          p: 2,
                          borderRadius: 3,
                          borderBottomLeftRadius: msg.sender === 'ai' ? 0 : 12,
                          borderBottomRightRadius: msg.sender === 'user' ? 0 : 12,
                          bgcolor: msg.sender === 'ai' ? 'white' : 'secondary.main',
                          color: msg.sender === 'ai' ? 'text.primary' : 'white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        }}
                      >
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{msg.text}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.6, fontSize: '0.65rem' }}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </ListItem>
                  ))}
                  
                  {isTyping && (
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 0 }}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'white', display: 'flex', gap: 0.5 }}>
                        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}><Box sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%' }} /></motion.div>
                        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}><Box sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%' }} /></motion.div>
                        <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}><Box sx={{ width: 6, height: 6, bgcolor: 'secondary.main', borderRadius: '50%' }} /></motion.div>
                      </Box>
                    </ListItem>
                  )}
                  <div ref={messagesEndRef} />
                </List>
              </Box>

              <Divider />

              {/* Input */}
              <Box sx={{ p: 2, bgcolor: 'white' }}>
                <TextField
                  fullWidth
                  placeholder="Ask about details, or schedule a visit..."
                  size="small"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  disabled={isTyping}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="secondary" onClick={handleSend} disabled={!inputValue.trim() || isTyping}>
                          <Send />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 8, bgcolor: 'grey.50' }
                  }}
                />
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      <Fab
        color="secondary"
        aria-label="chat"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          boxShadow: '0 8px 32px rgba(60, 62, 63, 0.4)',
          transition: 'all 0.3s',
          transform: isOpen ? 'scale(0.9) rotate(90deg)' : 'scale(1)',
          width: 64,
          height: 64,
        }}
      >
        {isOpen ? <Close /> : <Chat sx={{ fontSize: 32}} />}
      </Fab>
    </Box>
  );
};

export default PropertyAISalesmanWidget;
